<template>
  <div class="p-3 bg-gray-50 rounded-md border dark:bg-gray-900" :class="{ 'ring-offset-2 ring-primary-200 ring-4 rounded-md dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }">
    <o-field-group :label="$t('general.notes')" :name="$t('general.notes')" :info="$t('components.reservation.create.notes.fieldInfo')" rules="max:1000">
      <template #default="{ status }">
        <text-area-field
          v-model="_staging.notes"
          rows="4"
          placeholder="Add a note"
          class="mt-2"
          max-length="1000"
          :status="status"
        />
      </template>
    </o-field-group>

    <div v-if="previousNotes" class="flex justify-end mt-2">
      <o-dropdown ref="dropdown">
        <o-button size="sm" flat>
          {{ $t('components.reservation.create.notes.copyPrevious') }}
        </o-button>

        <template #content>
          <div
            class="max-h-[min(30rem,var(--reka-popover-content-available-height,30rem))] overflow-y-auto overscroll-contain"
          >
            <div
              v-for="note in previousNotes"
              :key="note.idNote"
              class="w-full flex items-center justify-between px-3 text-sm py-2 leading-none space-x-4"
            >
              <div>
                <p class="mb-1 text-gray-800 line-clamp-1 max-w-sm dark:text-gray-100">
                  {{ note.content }}
                </p>
                <p class="text-gray-600 dark:text-gray-300">
                  {{ note.dateLabel }}
                </p>
              </div>

              <o-button size="xs" flat icon="copy" @click="handleCopyNote(note.content)" />
            </div>
          </div>
        </template>
      </o-dropdown>
    </div>

    <template v-if="staging.customFields">
      <o-custom-fields :data="staging.customFields" class="w-full mt-4">
        <template #default="{fields}">
          <div
            v-for="field in fields"
            :key="field.props.id"
            class="flex space-x-4 mb-4"
          >
            <o-field-group class="w-full" :label="field.label">
              <component
                :is="field.component"
                v-bind="field.props"
                v-on="field.events"
              />
            </o-field-group>
          </div>
        </template>
      </o-custom-fields>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  OFieldGroup,
  OCustomFields,
} from '@/components/elements'

import {
  TextAreaField,
} from '@/components/fields'

import { useCustomFieldsStore } from '~/stores/useCustomFieldsStore'

const { $grants, $api } = useNuxtApp()

interface Props {
  staging: Record<string, any>
  isActive: boolean
}

interface Emits {
  (e: 'update:staging', value: any): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const customFieldsStore = useCustomFieldsStore()

const previousNotes = ref<any[] | null>(null)
const loading = ref(false)
const dropdown = ref<any>()
let latestNotesRequest = 0

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

const isCustomerTypeGroup = computed(() => props.staging.isCustomerTypeGroup)
const hasSingleService = computed(() => props.staging.hasSingleService)
const hasGroupService = computed(() => props.staging.hasGroupService)

watch(() => props.staging.services, () => {
  if (hasSingleService.value || isCustomerTypeGroup.value) {
    _staging.value.customFields = customFieldsStore.reservation
  } else if (hasGroupService.value) {
    _staging.value.customFields = customFieldsStore.reservationCustomer
  } else {
    _staging.value.customFields = null
  }
}, { deep: true })

watch(() => props.staging.customer, (customer) => {
  if (customer && $grants.all({ customer: ['profile'] })) {
    fetchNotes()
  } else {
    latestNotesRequest += 1
    previousNotes.value = null
    loading.value = false
  }
})

async function fetchNotes () {
  const requestId = ++latestNotesRequest
  loading.value = true

  try {
    const idCustomer = props.staging.customer?.idCustomer || props.staging.customer?.id
    const notes = idCustomer ? await $api.customer.combinedNotes(String(idCustomer)) : null

    if (requestId !== latestNotesRequest) return

    if (notes) {
      previousNotes.value = notes.items
    } else {
      previousNotes.value = null
    }
  } catch (e) {
    if (requestId !== latestNotesRequest) return

    console.log(e)
    previousNotes.value = null
  } finally {
    if (requestId === latestNotesRequest) {
      loading.value = false
    }
  }
}

function handleCopyNote (note: string) {
  _staging.value.notes += note

  dropdown.value.close()
}

onMounted(() => {
  customFieldsStore.sync()
})
</script>
